import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Form } from 'antd';
import { useForm } from 'antd/es/form/Form';

import { LimitFiles } from '../constants';
import { LoanDocState, ReplicationStrategy } from 'gql/graphql';
import CardUploadResponse from 'components/BorrowerTask/components/Atoms/Cards/CardUploadResponse/CardUploadResponse';
import InputBorderNone from 'components/BorrowerTask/components/Atoms/Inputs/InputBorderNone/InputBorderNone';
import { useCreateLoanDoc, useDeleteFileLoanDoc } from 'services/loan/mutations';
import { useGetLoanDocByLoan } from 'services/loan/querys';
import { useGetTasksByLoan } from 'services/task/querys';
import { LoanContext } from '../../LoanDetails';
import { useSequentialFileUpload } from '../hooks/useSequentialFileUpload';

interface IGenericDocumentFormProps {
  selectLoanDoc?: any;
  loanDocs?: any[];
  onSuccess?: () => void;
}

/**
 * Formulario para documentos genéricos sin key específica
 * Usado cuando el usuario crea documentos custom
 */
const GenericDocumentForm: React.FC<IGenericDocumentFormProps> = ({
  selectLoanDoc,
  loanDocs,
  onSuccess
}) => {
  const [form] = useForm();
  const loanSelected: any = useContext(LoanContext);

  const { execute: createLoanDoc } = useCreateLoanDoc();
  const { execute: deleteFileLoanDoc } = useDeleteFileLoanDoc();
  const { reexecute: getLoanDocByLoan } = useGetLoanDocByLoan({
    variables: { loanId: loanSelected?._id },
    pause: true
  });
  const { reexecute: getTasksByLoan } = useGetTasksByLoan({
    variables: { loanId: loanSelected?._id },
    pause: true
  });

  const [fileLoanDoc, setFileLoanDoc] = useState<any[] | undefined>(undefined);
  const [docName, setDocName] = useState<string>('');

  // Detectar si es edición (documento con _id)
  const isLoanDoc = selectLoanDoc?._id
    ? loanDocs?.find(item => item._id === selectLoanDoc._id)
    : undefined;

  // Hook de cola secuencial
  const uploadQueue = useSequentialFileUpload({
    documentKey: selectLoanDoc?.key,
    loanId: loanSelected._id,
    initialDocumentId: isLoanDoc?._id,
    state: LoanDocState.Approved,
    name: docName || selectLoanDoc?.label || selectLoanDoc?.name,
    isMultipleFiles: true,
    onUploadFile: createLoanDoc,
    onComplete: async () => {
      await Promise.all([
        getLoanDocByLoan({ requestPolicy: 'network-only' }),
        getTasksByLoan({ requestPolicy: 'network-only' })
      ]);
      
      // Solo cerrar modal si es creación de nuevo documento
      if (onSuccess && !selectLoanDoc?._id) {
        onSuccess();
      }
    },
    additionalPayload: {
      replicationStrategy: ReplicationStrategy.CopyToAll
    }
  });

  // Cargar datos existentes si es edición
  useEffect(() => {
    if (selectLoanDoc && selectLoanDoc._id) {
      form.setFieldValue("name", selectLoanDoc.label || selectLoanDoc.name);

      if (isLoanDoc?.description) {
        form.setFieldValue("description", isLoanDoc.description);
      }
    }
  }, [selectLoanDoc, isLoanDoc, form]);

  // Cargar archivos existentes de BD
  useEffect(() => {
    if (uploadQueue.isProcessing) return;
    
    if (isLoanDoc) {
      const initial: any[] | undefined = isLoanDoc.file && isLoanDoc.files?.length === 0
        ? [isLoanDoc.file]
        : isLoanDoc.files ? isLoanDoc.files : undefined;

      setFileLoanDoc(initial);
    }
  }, [isLoanDoc, loanDocs, uploadQueue.isProcessing]);

  const onFinish = async (files: any[]) => {
    const newFiles = files?.filter((item: any) => 
      !!item.size && !item.url && !item.key && !item.queueId && !item.isTemporary
    );

    if (!newFiles || newFiles.length === 0) return;

    const fileStates = uploadQueue.addFiles(newFiles);
    const tempFiles = fileStates.map(fs => ({
      name: fs.file.name,
      size: fs.file.size,
      type: fs.file.type,
      queueId: fs.id,
      isTemporary: true
    }));
        
    setFileLoanDoc(prev => {
      const keptFiles = prev?.filter(f => 
        f.url || f.key || f.isTemporary
      ) || [];
      
      const withoutProcessedFiles = keptFiles.filter(f => {
        if (f.isTemporary) return true;
        if (f.url || f.key) return true;
        return !newFiles.some((nf: File) => nf.name === f.name);
      });

      return [...withoutProcessedFiles, ...tempFiles];
    });

    // Solo resetear si es creación (no edición)
    if (!selectLoanDoc?._id) {
      form.resetFields();
      setDocName('');
    }
    
    await uploadQueue.startQueue(fileStates);
  };

  const onDeleteFile = async (fileToDelete: any) => {
    if (isLoanDoc && isLoanDoc.file) {
      await deleteFileLoanDoc({ fileKey: isLoanDoc.file.key, loanDocId: isLoanDoc._id });
      setFileLoanDoc([]);
    }
    if (isLoanDoc && isLoanDoc.files && isLoanDoc.files.length > 0 && fileToDelete?.key) {
      await deleteFileLoanDoc({ fileKey: fileToDelete.key, loanDocId: isLoanDoc._id, multipleFiles: true });
      setFileLoanDoc((prev) => prev?.filter((file) => file.key !== fileToDelete.key));
    }

    await Promise.all([
      getLoanDocByLoan({ requestPolicy: 'network-only' }),
      getTasksByLoan({ requestPolicy: 'network-only' })
    ]);
  };

  const isCardDisabled = () => {
    if (uploadQueue.isProcessing) return true;
    if (uploadQueue.allSuccess) return true;

    const newFilesCount = fileLoanDoc?.filter((file: any) => !file.url && !file.key).length || 0;

    // Modo creación: requiere nombre y archivo
    if (!selectLoanDoc?._id) {
      if (!docName || docName.trim() === '') return true;
      if (newFilesCount === 0) return true;
      return false;
    }

    // Modo edición: deshabilitar si no hay archivos nuevos
    if (newFilesCount === 0) return true;

    return false;
  };

  // Memoizar uploadQueueFiles para evitar recrearlo en cada render
  const uploadQueueFilesWithRetry = useMemo(() => {
    return uploadQueue.files.map(f => ({
      ...f,
      onRetry: () => uploadQueue.retryFile(f.id)
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadQueue.files, uploadQueue.updateCount]);

  return (
    <Form
      form={form}
      name="generic-doc-form"
      className='form'
      autoComplete="off"
      onFinish={onFinish}
    >
      <CardUploadResponse
        title=""
        titleLocation='outside'
        limitFiles={LimitFiles.MULTIPLE_FILES}
        actionDeleteFile={onDeleteFile}
        files={fileLoanDoc}
        uploadQueueFiles={uploadQueueFilesWithRetry}
        uploadQueue={uploadQueue}
        onChangeFiles={(files: any[]) => {
          setFileLoanDoc(files);
        }}
        submit={{
          title: selectLoanDoc?._id ? 'Publish to borrower' : 'Save change',
          disabled: isCardDisabled(),
          loading: uploadQueue.isProcessing,
          modalConfirm: true,
          confirmDescription: "After submitting, you'll have to contact us to make any changes.",
          onClick: (files) => onFinish(files),
          location: 'outside'
        }}
        isCreditReportDocument={false}
        isLoanDoc={undefined}
        hasDropdown={false}
        insertHtml={
          <div className='doc-info'>
            <Form.Item
              name="name"
              className='form-item-doc-name'
              style={{ marginBottom: 0 }}
              rules={[
                {
                  required: true,
                  message: 'Please input your doc name!'
                }
              ]}
            >
              <InputBorderNone
                placeholder="Enter doc name"
                className="form-task-name"
                color="bold"
                disabled={!!selectLoanDoc?._id}
                onChange={(e: any) => setDocName(e.target.value)}
                multiline
              />
            </Form.Item>

            <Form.Item
              name="description"
              style={{ marginBottom: 15 }}
            >
              <InputBorderNone
                placeholder="Enter document description (optional)"
                className="form-doc-description"
                disabled={!!selectLoanDoc?._id}
              />
            </Form.Item>
          </div>
        }
        messageErrorDisabled='Please complete all required fields'
        isSelectChanged={true}
      />
    </Form>
  );
};

export default GenericDocumentForm;